import type { FC } from "react";
import { useEffect, useState } from "react";
import { BooleanSearch } from "@similarweb/ui-components";
import type { IBooleanSearchChipItem } from "@similarweb/ui-components";
import { StatefulDropdown } from "app/components/dashboard/widget-wizard/components/StatefulDropdown";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { radioItems } from "app/components/React/PopularPagesFilters/popularPagesFilters.config";
import _ from "lodash";
import {
    booleanSearchApiParamsToChips,
    booleanSearchChipsObjectToApiParams,
} from "app/pages/website-analysis/traffic-sources/search/booleanSearchUtility";
import { BooleanSearchWrapper } from "../widget-types/TableSearchKeywordsDashboardWidgetFilters";
import { Widget } from "../widget-types/Widget";

const ALL_SEARCH_CHANNELS_ID = "0";

interface IWidgetPreviewFiltersSource {
    count: number;
    id: string;
    text: string;
}

interface IWidgetFilters {
    filter: string;
    includeBranded: boolean | string;
    isAll: boolean;
    orderBy: string;
    includeNoneBranded: boolean | string;
    ExcludeTerms: string;
    IncludeTerms: string;
}

interface IWidgetPreview {
    Filters: { Source: IWidgetPreviewFiltersSource[] };
    Header: string;
    Items: string;
    Records: unknown;
    TotalCount: number;
    maxCellValue: number;
    minCellValue: number;
    page: number;
    pageSize: number;
}

interface IIndustryKeywordsDashboardTableWidgetFilters {
    widgetFilters: IWidgetFilters;
    widgetPreview: IWidgetPreview;
    widgetMetric: string;
    changeFilter: (filter) => void;
}

interface ISelectedFilter {
    filter?: unknown;
    includeBranded?: boolean;
    includeNoneBranded?: boolean;
    isAll?: boolean;
    orderBy?: string;
    IncludeTerms?: string;
    ExcludeTerms?: string;
}

const IndustryKeywordsDashboardTableWidgetFilters: FC<IIndustryKeywordsDashboardTableWidgetFilters> = ({
    widgetFilters,
    widgetPreview,
    widgetMetric,
    changeFilter,
}) => {
    const getInitialSelection = () => {
        const filterObject = Widget.filterParse(widgetFilters.filter);
        const filterObjectItem: { operator: string; value: string } = filterObject["Source"];
        return filterObjectItem ? filterObjectItem.value : ALL_SEARCH_CHANNELS_ID;
    };

    const [includeBranded, setIncludeBranded] = useState();
    const [source, setSource] = useState(getInitialSelection());
    const [dynamicSearchChannels, setDynamicSearchChannels] = useState<any>(
        widgetPreview && widgetPreview.Filters ? widgetPreview.Filters.Source : "",
    );
    const [selectedFilters, setSelectedFilters] = useState<ISelectedFilter>({});

    const defaultMetricFilters = {
        TopKeywordsOrganic: "OP;==;0",
        TopKeywordsPaid: "OP;==;1",
    };

    useEffect(() => {
        let { includeBranded = "true" }: any = widgetFilters;
        const { includeNoneBranded } = widgetFilters;
        includeBranded = /true|false/i.test(includeBranded.toString()) ? includeBranded.toString() : "true";

        setIncludeBranded(includeBranded);

        const newSelectedFilters: ISelectedFilter = {
            filter: widgetFilters.filter,
            orderBy: widgetFilters.orderBy || "TotalShare desc",
            includeBranded: Boolean(includeBranded),
            includeNoneBranded: Boolean(includeNoneBranded),
        };

        if (widgetFilters.ExcludeTerms) {
            newSelectedFilters.ExcludeTerms = widgetFilters.ExcludeTerms;
        }
        if (widgetFilters.IncludeTerms) {
            newSelectedFilters.IncludeTerms = widgetFilters.IncludeTerms;
        }
        newSelectedFilters[getSelectedFilter()] = true;
        changeFilter(getSelectedFilters());
        setSelectedFilters(newSelectedFilters);
    }, []);

    useEffect(() => {
        if (widgetPreview && widgetPreview !== dynamicSearchChannels && widgetPreview.Filters) {
            const newSearchChannels = [
                { id: ALL_SEARCH_CHANNELS_ID, text: "All Search Channels" },
                ...widgetPreview.Filters.Source,
            ];

            setDynamicSearchChannels(newSearchChannels);
            setSource(source || ALL_SEARCH_CHANNELS_ID);
        }
    }, [widgetMetric]);

    const getSearchValue = () => {
        const filterObject = Widget.filterParse(selectedFilters.filter);
        const filterObjectItem = _.find(filterObject, { operator: "contains" });
        return filterObjectItem && filterObjectItem.hasOwnProperty("value")
            ? filterObjectItem.value.replaceAll(/"/g, "")
            : "";
    };

    const getSelectedFilters = (newSource?) => {
        const filters = [];
        for (const filter of Object.keys(selectedFilters)) {
            let value = selectedFilters[filter];
            if (filter === "filter") {
                const metricFilter = defaultMetricFilters[widgetMetric] ? `${defaultMetricFilters[widgetMetric]},` : "";
                const textFilter = getSearchValue() ? `SearchTerm;contains;"${getSearchValue()}",` : "";
                let sourceFilter;
                if (typeof newSource !== "undefined") {
                    sourceFilter = parseInt(newSource, 10) > 0 ? `Source;==;${newSource},` : "";
                } else {
                    sourceFilter = parseInt(source, 10) > 0 ? `Source;==;${source},` : "";
                }
                value = `${textFilter}${metricFilter}${sourceFilter}`;
                value = _.trim(value, ",");
            }
            filters.push({ name: filter, value });
        }
        return filters;
    };

    const onSearch = (chipsObject: IBooleanSearchChipItem[]) => {
        const { IncludeTerms, ExcludeTerms } = booleanSearchChipsObjectToApiParams(chipsObject);
        if (IncludeTerms === "") {
            delete selectedFilters.IncludeTerms;
        } else {
            selectedFilters.IncludeTerms = IncludeTerms;
        }
        if (ExcludeTerms === "") {
            delete selectedFilters.ExcludeTerms;
        } else {
            selectedFilters.ExcludeTerms = ExcludeTerms;
        }

        changeFilter(getSelectedFilters());
    };

    const getSelectedFilter = () => {
        let selectedFilter = radioItems[0].value;
        radioItems.forEach((radioItem) => {
            if (widgetFilters[radioItem.value] === true) {
                selectedFilter = radioItem.value;
                return;
            }
        });
        return selectedFilter;
    };

    const onSelectincludeBranded = (item) => {
        setIncludeBranded(item.id);
        selectedFilters.includeBranded = item.id === "true";
        selectedFilters.includeNoneBranded = !selectedFilters.includeBranded;
        changeFilter(getSelectedFilters());
    };

    const onSelectSource = (item) => {
        setSource(item.id);
        changeFilter(getSelectedFilters(item.id));
    };

    const onSelectOrderBy = (item) => {
        selectedFilters.orderBy = item.id;
        changeFilter(getSelectedFilters());
    };

    const searchChannels =
        dynamicSearchChannels?.length > 0 ? (
            <div
                key="searchChannel"
                className="dynamicFilter"
            >
                <h5>
                    <I18n>{`home.dashboards.wizard.filters.searchChannel`}</I18n>
                </h5>
                <StatefulDropdown
                    key="sourceFilter"
                    items={dynamicSearchChannels}
                    selectedId={source}
                    onSelect={onSelectSource}
                />
            </div>
        ) : null;
    return (
        <div
            key="dynamicFiltersContainer"
            className="filters"
        >
            <div
                key="includeBranded"
                className="dynamicFilter"
            >
                <h5>
                    <I18n>{`home.dashboards.wizard.filters.includeBranded`}</I18n>
                </h5>
                <StatefulDropdown
                    key="includeBrandedFilter"
                    items={[
                        { id: "true", text: "Yes" },
                        { id: "false", text: "No" },
                    ]}
                    selectedId={includeBranded}
                    onSelect={onSelectincludeBranded}
                />
            </div>
            {searchChannels}
            <div
                key="orderby"
                className="dynamicFilter"
            >
                <h5>
                    <I18n>{`home.dashboards.wizard.filters.orderBy`}</I18n>
                </h5>
                <StatefulDropdown
                    key="orderbyFilter"
                    items={[
                        { id: "TotalShare desc", text: "Traffic Share" },
                        { id: "Change desc", text: "Change" },
                    ]}
                    selectedId={selectedFilters.orderBy}
                    onSelect={onSelectOrderBy}
                />
            </div>
            <div
                key="searchterm"
                className="filters"
                style={{ flexDirection: "column" }}
            >
                <h5>
                    <I18n>{`home.dashboards.wizard.filters.searchterm`}</I18n>
                </h5>
                <BooleanSearchWrapper>
                    <BooleanSearch
                        onChange={onSearch}
                        chips={booleanSearchApiParamsToChips({
                            IncludeTerms: selectedFilters.IncludeTerms,
                            ExcludeTerms: selectedFilters.ExcludeTerms,
                        })}
                    />
                </BooleanSearchWrapper>
            </div>
        </div>
    );
};

export default IndustryKeywordsDashboardTableWidgetFilters;
